import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  ContactConversationMessage,
  ContactConversationPage,
  ContactConversationPageParams,
  DashboardCapabilities,
  ContactFilterFacets,
  InboxConversation,
  InboxConversationPage,
  InboxSummary,
  InboxTransitionInput,
  InboxTransitionResult,
  ListInboxConversationsParams,
  Deal,
  DealNote,
  EchoflicksSessionRemake,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
  SendWhatsAppMessageInput,
  SendWhatsAppMessageResult,
  TestEchoLaunch,
  TestEchoLaunchRequest,
  TestEchoLaunchStatus,
  TestEchoPack,
  WhatsAppBroadcast,
  WhatsAppBroadcastAnalytics,
  WhatsAppBroadcastInput,
  WhatsAppBroadcastPreview,
  WhatsAppBroadcastRecipient,
  WhatsAppBroadcastTimelineBucket,
  WhatsAppMessagingCapability,
  WhatsAppOutboundMessage,
  WhatsAppSenderCatalog,
  WhatsAppTemplate,
  WhatsAppTemplateInput,
  WhatsAppTemplateLibraryFilters,
  WhatsAppTemplateLibraryImport,
  WhatsAppTemplateLibraryImportInput,
  WhatsAppTemplateLibraryImportResult,
  WhatsAppTemplateLibraryImportSyncResult,
  WhatsAppTemplateLibraryPage,
  WhatsAppTemplateMediaAsset,
  WhatsAppTemplateMediaUploadInput,
} from "../../types";
import type {
  DurableRecoveryAction,
  DurableRecoveryActionRequest,
} from "../../echoflicks/durableEchoLedger";
import type { DurableEchoLedgerProjection } from "../../echoflicks/durableEchoLedger";
import type { EchoWebTranscript } from "../../echoflicks/webTranscript";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import {
  createAwsDashboardDataProvider,
  isAwsDashboardDataProviderEnabled,
} from "../aws";
import { getIsInitialized } from "./authProvider";
import { getAuthRedirectUrl } from "./authRedirect";
import { getSupabaseClient } from "./supabase";
import {
  EMPTY_LIST_RESULT,
  resolveSavedEchoesFilter,
} from "../commons/savedEchoes";

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const isSignupDisabled = import.meta.env.VITE_DISABLE_SIGNUP !== "false";

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "echoflicks_video_sessions") {
        return baseDataProvider.getList(
          "echoflicks_video_sessions_summary",
          params,
        );
      }
      if (resource === "echoflicks_echoes") {
        // "Saved echoes" toggle → the user's saved ids as an id filter.
        const resolved = await resolveSavedEchoesFilter(params);
        if (resolved.empty) return EMPTY_LIST_RESULT;
        return baseDataProvider.getList(
          "echoflicks_echoes_summary",
          resolved.params,
        );
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }
      if (resource === "echoflicks_video_sessions") {
        return baseDataProvider.getOne(
          "echoflicks_video_sessions_summary",
          params,
        );
      }
      if (resource === "echoflicks_echoes") {
        return baseDataProvider.getOne("echoflicks_echoes_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      if (isSignupDisabled) {
        throw new Error("Account creation is disabled for this POC.");
      }

      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
          emailRedirectTo: getAuthRedirectUrl(),
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw new Error(errorDetails?.message || "Failed to create the user");
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const {
        email,
        first_name,
        last_name,
        administrator,
        avatar,
        disabled,
        blog_role,
      } = data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            first_name,
            last_name,
            administrator,
            disabled,
            blog_role,
            avatar,
          },
        });

      if (!updatedData || error) {
        console.error("salesCreate.error", error);
        throw new Error("Failed to update account manager");
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
    async getWhatsAppMessagingCapability(
      contactId: Identifier,
      senderKey?: string,
    ): Promise<WhatsAppMessagingCapability> {
      try {
        const { data, error } = await getSupabaseClient().functions.invoke<{
          data: WhatsAppMessagingCapability;
        }>("whatsapp_messaging", {
          method: "POST",
          body: {
            action: "capability",
            contact_id: contactId,
            sender_key: senderKey,
          },
        });

        if (error) {
          throw error;
        }
        if (data?.data) {
          return data.data;
        }
      } catch {
        // Local Supabase mode is optional for real WhatsApp sends. Return a
        // clear blocked capability when the Edge Function is not configured.
      }

      return {
        contact_id: contactId,
        sender_key: senderKey,
        whatsapp_user_id: null,
        opt_in_status: "unknown",
        last_inbound_at: null,
        last_outbound_at: null,
        freeform_window_expires_at: null,
        can_send_freeform: false,
        requires_template: true,
        block_reasons: ["local_whatsapp_function_not_configured"],
      };
    },
    async getWhatsAppSenders(): Promise<WhatsAppSenderCatalog> {
      return invokeWhatsAppMessaging<WhatsAppSenderCatalog>(
        "sender_catalog",
        {},
        {
          schema_version: "whatsapp-sender-catalog.v1",
          default_sender_key: "",
          senders: [],
          config_release_id: "local-unavailable",
          config_digest: "local-unavailable",
          config_schema_version: 0,
        },
      );
    },
    async sendWhatsAppMessage(
      payload: SendWhatsAppMessageInput,
    ): Promise<SendWhatsAppMessageResult> {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: SendWhatsAppMessageResult;
      }>("whatsapp_messaging", {
        method: "POST",
        body: {
          action: "send",
          ...payload,
        },
      });

      if (error || !data?.data) {
        throw new Error(
          error?.message ||
            "WhatsApp sending is not configured for local Supabase mode",
        );
      }

      return data.data;
    },
    async getContactOutboundMessages(
      contactId: Identifier,
    ): Promise<WhatsAppOutboundMessage[]> {
      try {
        const { data, error } = await getSupabaseClient().functions.invoke<{
          data: WhatsAppOutboundMessage[];
        }>("whatsapp_messaging", {
          method: "POST",
          body: {
            action: "outbound_messages",
            contact_id: contactId,
          },
        });

        if (error) {
          throw error;
        }
        return data?.data ?? [];
      } catch {
        return [];
      }
    },
    async getContactConversation(
      contactId: Identifier,
    ): Promise<ContactConversationMessage[]> {
      try {
        const { data, error } = await getSupabaseClient().functions.invoke<{
          data: ContactConversationMessage[];
        }>("whatsapp_messaging", {
          method: "POST",
          body: {
            action: "conversation",
            contact_id: contactId,
          },
        });

        if (error) {
          throw error;
        }
        return data?.data ?? [];
      } catch {
        return [];
      }
    },
    async getContactConversationPage(
      contactId: Identifier,
      _params: ContactConversationPageParams = {},
    ): Promise<ContactConversationPage> {
      // The Supabase edge function has no cursor pagination: return the whole
      // thread as a single page.
      const data = await this.getContactConversation(contactId);
      return { data, has_more: false, next_cursor: null };
    },
    // The customer messages inbox is an AWS-dashboard-only feature (the
    // `whatsapp_inbox` capability stays off here); these stubs keep the
    // CrmDataProvider type honest.
    async listInboxConversations({
      state,
    }: ListInboxConversationsParams): Promise<InboxConversationPage> {
      return { data: [], total: 0, state, has_more: false, next_cursor: null };
    },
    async getInboxSummary(): Promise<InboxSummary> {
      return {
        needs_reply: 0,
        claimed: 0,
        resolved: 0,
        needs_reply_by_priority: { high: 0, normal: 0, low: 0 },
        oldest_needs_reply_since: null,
        generated_at: null,
      };
    },
    async getInboxConversation(
      contactId: Identifier,
    ): Promise<InboxConversation> {
      return {
        id: String(contactId),
        contact_id: contactId,
        whatsapp_user_id: String(contactId),
        attention_state: "none",
      };
    },
    async claimInboxConversation(
      _contactId: Identifier,
      _input?: InboxTransitionInput,
    ): Promise<InboxTransitionResult> {
      throw new Error("The customer inbox is not supported on this backend");
    },
    async resolveInboxConversation(
      _contactId: Identifier,
      _input?: InboxTransitionInput,
    ): Promise<InboxTransitionResult> {
      throw new Error("The customer inbox is not supported on this backend");
    },
    async reopenInboxConversation(
      _contactId: Identifier,
      _input?: InboxTransitionInput,
    ): Promise<InboxTransitionResult> {
      throw new Error("The customer inbox is not supported on this backend");
    },
    async releaseInboxConversation(
      _contactId: Identifier,
      _input?: InboxTransitionInput,
    ): Promise<InboxTransitionResult> {
      throw new Error("The customer inbox is not supported on this backend");
    },
    async listWhatsAppTemplates(): Promise<WhatsAppTemplate[]> {
      return invokeWhatsAppMessaging("templates", {}, []);
    },
    async getDashboardCapabilities(): Promise<DashboardCapabilities> {
      return {
        ok: false,
        environment: "local",
        features: {
          whatsapp_messages: false,
          whatsapp_conversation: false,
          whatsapp_templates: false,
          whatsapp_broadcasts: false,
          whatsapp_broadcast_worker: false,
          whatsapp_broadcast_analytics: false,
          whatsapp_inbox: false,
          broadcast_cohort_filters: false,
          contact_echo_metrics: true,
          contact_metric_range_filters: true,
          contact_metric_sorting: true,
          contact_echo_segments_v2: true,
          template_catalog_sync: false,
          template_media_upload: false,
          template_library: false,
          template_library_import: false,
          scheduled_broadcasts: false,
        },
      };
    },
    async getContactFilterFacets(): Promise<ContactFilterFacets> {
      // Supabase backend has no facets endpoint; the capability stays off so the
      // sidebar never asks for counts here.
      return { facets: {}, total: 0 };
    },
    async listWhatsAppTemplateLibrary(
      _filters: WhatsAppTemplateLibraryFilters = {},
    ): Promise<WhatsAppTemplateLibraryPage> {
      return { data: [], total: 0, next_cursor: null, provider: null };
    },
    async importWhatsAppTemplateFromLibrary(
      _payload: WhatsAppTemplateLibraryImportInput,
    ): Promise<WhatsAppTemplateLibraryImportResult> {
      throw new Error("WhatsApp template library is not configured");
    },
    async listWhatsAppTemplateLibraryImports(): Promise<
      WhatsAppTemplateLibraryImport[]
    > {
      return [];
    },
    async syncWhatsAppTemplateLibraryImport(
      _id: Identifier,
    ): Promise<WhatsAppTemplateLibraryImportSyncResult> {
      throw new Error("WhatsApp template library is not configured");
    },
    async syncAllWhatsAppTemplates(): Promise<WhatsAppTemplate[]> {
      return invokeWhatsAppMessaging("sync_all_templates", {}, []);
    },
    async createWhatsAppTemplate(
      payload: WhatsAppTemplateInput,
    ): Promise<WhatsAppTemplate> {
      return invokeWhatsAppMessaging("create_template", payload);
    },
    async uploadWhatsAppTemplateMedia(
      payload: WhatsAppTemplateMediaUploadInput,
    ): Promise<WhatsAppTemplateMediaAsset> {
      return invokeWhatsAppMessaging("upload_template_media", payload);
    },
    async updateWhatsAppTemplate(
      id: Identifier,
      payload: Partial<WhatsAppTemplateInput>,
    ): Promise<WhatsAppTemplate> {
      return invokeWhatsAppMessaging("update_template", { id, ...payload });
    },
    async syncWhatsAppTemplate(id: Identifier): Promise<WhatsAppTemplate> {
      return invokeWhatsAppMessaging("sync_template", { id });
    },
    async unpauseWhatsAppTemplate(id: Identifier): Promise<WhatsAppTemplate> {
      return invokeWhatsAppMessaging("unpause_template", { id });
    },
    async addWhatsAppTemplateAppealNote(
      id: Identifier,
      note: string,
      operator?: { id?: Identifier; fullName?: string },
    ): Promise<WhatsAppTemplate> {
      return invokeWhatsAppMessaging("template_appeal_note", {
        id,
        note,
        operator_user_id: operator?.id,
        operator_name: operator?.fullName,
      });
    },
    async previewWhatsAppBroadcast(
      payload: WhatsAppBroadcastInput,
    ): Promise<WhatsAppBroadcastPreview> {
      return invokeWhatsAppMessaging("broadcast_preview", payload);
    },
    async createWhatsAppBroadcast(
      payload: WhatsAppBroadcastInput,
    ): Promise<WhatsAppBroadcast> {
      return invokeWhatsAppMessaging("create_broadcast", payload);
    },
    async updateWhatsAppBroadcast(
      id: Identifier,
      payload: WhatsAppBroadcastInput,
    ): Promise<WhatsAppBroadcast> {
      return invokeWhatsAppMessaging("update_broadcast", {
        ...payload,
        broadcast_id: id,
      });
    },
    async listWhatsAppBroadcasts(): Promise<WhatsAppBroadcast[]> {
      return invokeWhatsAppMessaging("broadcasts", {}, []);
    },
    async getWhatsAppBroadcastRecipients(
      id: Identifier,
    ): Promise<WhatsAppBroadcastRecipient[]> {
      return invokeWhatsAppMessaging("broadcast_recipients", { id }, []);
    },
    async getWhatsAppBroadcastAnalytics(
      id: Identifier,
    ): Promise<WhatsAppBroadcastAnalytics> {
      return invokeWhatsAppMessaging("broadcast_analytics", { id }, {
        id,
        broadcast_id: id,
        recipient_count: 0,
        eligible_count: 0,
        skipped_count: 0,
        queued_count: 0,
        sending_count: 0,
        accepted_count: 0,
        held_count: 0,
        sent_count: 0,
        delivered_count: 0,
        read_count: 0,
        failed_count: 0,
        cancelled_count: 0,
        inbound_count: 0,
        create_command_count: 0,
        echo_started_count: 0,
        generation_requested_count: 0,
        final_video_created_count: 0,
        final_video_delivered_count: 0,
        ignored_delivered_unread_count: 0,
        ignored_read_no_action_count: 0,
        ignored_no_conversion_count: 0,
      } satisfies WhatsAppBroadcastAnalytics);
    },
    async getWhatsAppBroadcastTimeline(
      id: Identifier,
    ): Promise<WhatsAppBroadcastTimelineBucket[]> {
      return invokeWhatsAppMessaging("broadcast_timeline", { id }, []);
    },
    async exportWhatsAppBroadcastRecipients(id: Identifier): Promise<string> {
      return invokeWhatsAppMessaging("broadcast_export", { id }, "");
    },
    async transitionWhatsAppBroadcast(
      id: Identifier,
      action: "pause" | "resume" | "cancel" | "retry-failed",
    ): Promise<WhatsAppBroadcast> {
      return invokeWhatsAppMessaging("broadcast_transition", { id, action });
    },
    async getDurableEchoLedger(
      _workloadId: Identifier,
    ): Promise<DurableEchoLedgerProjection | null> {
      return null;
    },
    async getDurableEchoRecoveryActions(
      _workloadId: Identifier,
    ): Promise<DurableRecoveryAction[]> {
      return [];
    },
    async createDurableEchoRecoveryAction(
      _workloadId: Identifier,
      _request: DurableRecoveryActionRequest,
    ): Promise<Record<string, unknown>> {
      throw new Error(
        "Durable recovery actions are not supported on this backend",
      );
    },
    // The web Create timeline (MYC-090) lives behind the AWS dashboard-api
    // only; no Supabase Echo is a web Echo, so the answer is "not a web Echo".
    async getEchoWebTranscript(
      _workloadId: Identifier,
    ): Promise<EchoWebTranscript | null> {
      return null;
    },
    // Echo re-make (Level-1 composer replay) is an AWS-dashboard-only feature —
    // it depends on the dashboard-api /video-sessions/{id}/remakes endpoints and
    // has no Supabase backend. These stubs keep the CrmDataProvider type honest;
    // the real implementations live in providers/aws/dataProvider.ts.
    async triggerSessionRemake(
      _sessionId: Identifier,
      _operator?: { id?: Identifier },
    ): Promise<EchoflicksSessionRemake> {
      throw new Error("Echo remakes are not supported on this backend");
    },
    async approveSessionRemake(
      _sessionId: Identifier,
      _remakeId: Identifier,
      _operator?: { id?: Identifier },
    ): Promise<EchoflicksSessionRemake> {
      throw new Error("Echo remakes are not supported on this backend");
    },
    async rejectSessionRemake(
      _sessionId: Identifier,
      _remakeId: Identifier,
      _operator?: { id?: Identifier },
    ): Promise<EchoflicksSessionRemake> {
      throw new Error("Echo remakes are not supported on this backend");
    },
    // The test-echo autoqueue talks to the WhatsApp bot's internal API through
    // the CRM's own Vercel proxy (src/server/testEchoProxy.ts); there is no
    // Supabase backend for it. Stubs keep the CrmDataProvider type honest.
    async listTestEchoPacks(_userId: string): Promise<TestEchoPack[]> {
      throw new Error("Test echoes are not supported on this backend");
    },
    async launchTestEcho(
      _request: TestEchoLaunchRequest,
    ): Promise<TestEchoLaunch> {
      throw new Error("Test echoes are not supported on this backend");
    },
    async getTestEchoLaunch(
      _workloadId: string,
      _userId: string,
      _senderKey?: string,
    ): Promise<TestEchoLaunchStatus> {
      throw new Error("Test echoes are not supported on this backend");
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

async function invokeWhatsAppMessaging<T>(
  action: string,
  body: Record<string, unknown>,
): Promise<T>;

async function invokeWhatsAppMessaging<T>(
  action: string,
  body: Record<string, unknown>,
  fallback: T,
): Promise<T>;

async function invokeWhatsAppMessaging<T>(
  action: string,
  body: Record<string, unknown>,
  fallback?: T,
): Promise<T> {
  try {
    const { data, error } = await getSupabaseClient().functions.invoke<{
      data: T;
    }>("whatsapp_messaging", {
      method: "POST",
      body: {
        action,
        ...body,
      },
    });

    if (error || data?.data == null) {
      throw new Error(
        error?.message || "WhatsApp CRM function is not configured",
      );
    }
    return data.data;
  } catch (error) {
    if (arguments.length === 3) {
      return fallback as T;
    }
    throw error;
  }
}

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "echoflicks_video_sessions",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "whatsapp_user_id",
        "contact_name",
        "movie_title",
        "script_title",
        "session_id",
        "workload_id",
      ])(params);
    },
  },
  {
    resource: "echoflicks_echoes",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "whatsapp_user_id",
        "contact_name",
        "movie_title",
        "script_title",
        "session_id",
        "workload_id",
      ])(params);
    },
  },
  {
    resource: "subscribers",
    beforeGetList: async (params) => {
      if (!params.filter?.q) {
        return params;
      }
      const { q, ...filter } = params.filter;
      return {
        ...params,
        filter: {
          ...filter,
          "email@ilike": q,
        },
      };
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  const supabaseDataProvider = withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks,
  ) as CrmDataProvider;

  if (isAwsDashboardDataProviderEnabled()) {
    return createAwsDashboardDataProvider(supabaseDataProvider);
  }

  return supabaseDataProvider;
};

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce((acc, column) => {
        if (column === "email")
          return {
            ...acc,
            [`email_fts@ilike`]: q,
          };
        if (column === "phone")
          return {
            ...acc,
            [`phone_fts@ilike`]: q,
          };
        else
          return {
            ...acc,
            [`${column}@ilike`]: q,
          };
      }, {}),
    },
  };
};

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
